import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  ALLERGEN_SAFETY_QA_GUIDE_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${ALLERGEN_SAFETY_PILLAR_PATH}`;
const qaGuideUrl = `https://speakyourmenu.com${ALLERGEN_SAFETY_QA_GUIDE_PATH}`;
const pageTitle = "Restaurant Allergen Safety Guide | Speak Your Menu";
const pageDescription = "A practical allergen-safety guide for restaurants that need more accurate dietary answers, safer substitutions, and stronger guest trust during service.";

const jumpLinks = [
  { href: "#what-it-is", label: "What it is" },
  { href: "#why-it-fails", label: "Why it fails" },
  { href: "#system", label: "The system" },
  { href: "#service-rhythm", label: "Service rhythm" },
  { href: "#mistakes", label: "Common mistakes" },
  { href: "#faq", label: "FAQ" },
];

const keyTakeaways = [
  "Allergen safety breaks down when ingredient answers, substitution rules, and escalation habits vary from one server or shift to the next.",
  "The strongest system combines accurate menu data, staff practice on guest-facing language, and clear rules for when to stop and verify.",
  "A safer training process should reduce hesitation, improve answer consistency, and build guest trust without promising medical or legal certainty.",
];

const systemSteps = [
  {
    title: "Set one source of truth",
    body: "Define which allergens, ingredients, and modification rules apply to each dish so staff are not improvising answers from memory.",
  },
  {
    title: "Teach answer language, not just facts",
    body: "Servers need clear phrases for what is in a dish, what can be modified safely, and when they must confirm with the kitchen.",
  },
  {
    title: "Reinforce the highest-risk questions",
    body: "Repeat the dishes, substitutions, and allergy scenarios that surface most often so accuracy holds up during busy service.",
  },
  {
    title: "Make escalation non-negotiable",
    body: "A strong system teaches when not to guess, who verifies the answer, and how that handoff should sound to the guest.",
  },
];

const serviceRhythm = [
  "Day 1: Confirm allergen tags, ingredient details, and modification rules for the dishes that generate the most guest questions.",
  "Day 2: Give staff a short answer framework for explaining what is in a dish and when they need to verify with the kitchen.",
  "Day 3: Practice the highest-risk dietary questions so the same answer standard appears across the whole floor.",
  "Day 4: Reinforce substitution rules and the language staff should use when a modification is not safe or not guaranteed.",
  "Day 5: Review where hesitation or conflicting answers still appear and coach those exact dishes before the weekend rush.",
  "Day 6: Use pre-shift reinforcement to revisit allergy questions, guest trust language, and escalation habits by role.",
  "Day 7: Turn the strongest answer patterns into a repeatable weekly allergen-safety rhythm for managers and staff.",
];

const mistakes = [
  "Treating allergy questions like general menu knowledge instead of a higher-stakes communication standard.",
  "Letting staff guess when they are unsure about ingredients, substitutions, or cross-contact risk.",
  "Failing to define which modifications are safe, unsafe, or require direct kitchen confirmation.",
  "Assuming one training session is enough even though menu changes and service pressure quickly erode recall.",
];

const faqs = [
  {
    question: "What should allergen training cover first?",
    answer: "Start with the dishes and questions that come up most often, the allergens guests ask about most, and the exact modification rules staff need before service gets busy.",
  },
  {
    question: "Should servers answer every allergy question on their own?",
    answer: "No. A strong system teaches both what they can answer clearly and when they must stop, verify, and escalate so the guest gets a safer and more reliable response.",
  },
  {
    question: "Why do allergy answers still vary even when the menu is documented?",
    answer: "Because documentation alone does not create consistent service language. Staff need repeated practice on what to say, what not to promise, and how to verify when unsure.",
  },
];

export default function AllergenSafetyPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Allergen Safety Guide", item: canonicalUrl },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Restaurant Allergen Safety Guide: A Practical System for More Accurate Dietary Answers and Safer Service",
    description: pageDescription,
    author: { "@type": "Organization", name: "Speak Your Menu" },
    publisher: { "@type": "Organization", name: "Speak Your Menu" },
    mainEntityOfPage: canonicalUrl,
    dateModified: new Date().toISOString(),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Allergen Safety Systems</span>
              <h1 className="homepage-hero-title resource-hero-title">Restaurant allergen training that helps staff answer with more confidence.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                This guide shows restaurant operators how to build an allergen-safety training system that improves answer consistency, substitution clarity, and guest trust without asking staff to guess under pressure.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link">Open the Q&amp;A guide</Link>
              </div>
              <div className="resource-jump-card homepage-surface-card">
                <p className="resource-jump-kicker">In this guide</p>
                <div className="resource-jump-links" aria-label="Jump to sections">
                  {jumpLinks.map((link) => (
                    <a key={link.href} href={link.href}>{link.label}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card" id="what-it-is">
              <span className="homepage-label-chip">Key takeaways</span>
              <h2 className="homepage-section-title resource-section-title">What a strong allergen-safety training system should do</h2>
              <ul className="resource-bullet-list">
                {keyTakeaways.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <aside className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Related resource</span>
              <h2 className="homepage-card-title resource-aside-title">Need the shorter execution layer?</h2>
              <p className="homepage-card-copy">Use the Q&amp;A guide when you want a quicker manager reference for dietary questions, substitutions, and escalation language.</p>
              <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="homepage-primary-cta resource-inline-cta">View Q&amp;A guide</Link>
            </aside>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="why-it-fails">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Why it fails</span>
            <h2 className="homepage-section-title resource-section-title">The usual problem is not care. It is answer inconsistency.</h2>
            <p className="homepage-section-body resource-section-body">
              Most teams care deeply about guest safety, but service still breaks down when ingredient details live in too many places, modification rules are unclear, and employees rely on memory instead of a shared standard. That is when one guest hears one answer from one server and a different answer from another.
            </p>
            <p className="homepage-section-body resource-section-body">
              The operational risk is not just missing knowledge. It is inconsistent communication under pressure. A better training system fixes that by defining one source of truth, reinforcing guest-facing language, and making escalation a normal part of safe service.
            </p>
          </div>
        </section>

        <section className="homepage-section" id="system">
          <div className="homepage-shell">
            <span className="homepage-label-chip">The system</span>
            <h2 className="homepage-section-title resource-section-title">A practical four-part system for restaurant allergen safety training</h2>
            <div className="resource-step-grid">
              {systemSteps.map((step, index) => (
                <article key={step.title} className="homepage-surface-card resource-step-card">
                  <div className="homepage-step-number">0{index + 1}</div>
                  <h3 className="homepage-card-title">{step.title}</h3>
                  <p className="homepage-card-copy">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="service-rhythm">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Service rhythm</span>
            <h2 className="homepage-section-title resource-section-title">How to turn allergen safety into a repeatable weekly service rhythm</h2>
            <ol className="resource-numbered-list">
              {serviceRhythm.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </section>

        <section className="homepage-section" id="mistakes">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Common mistakes</span>
              <h2 className="homepage-section-title resource-section-title">Four mistakes that make allergen answers less reliable during service</h2>
              <ul className="resource-bullet-list">
                {mistakes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="homepage-surface-card resource-card resource-card-contrast">
              <span className="homepage-label-chip">Next action</span>
              <h2 className="homepage-card-title resource-aside-title">Turn this into a table-side answer guide your team can use right away</h2>
              <p className="homepage-card-copy">If you want the shorter operating layer, use the Q&amp;A guide to reinforce what staff should say, what they should verify, and what they should never guess.</p>
              <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link resource-secondary-link-inline">Go to Q&amp;A guide</Link>
              <a href={qaGuideUrl} className="resource-tertiary-link">Share the Q&amp;A guide URL</a>
            </article>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="faq">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">FAQ</span>
            <h2 className="homepage-section-title resource-section-title">Questions operators ask when tightening allergen and dietary answers</h2>
            <div className="resource-faq-stack">
              {faqs.map((item) => (
                <article key={item.question} className="homepage-surface-card resource-faq-card">
                  <h3 className="homepage-card-title">{item.question}</h3>
                  <p className="homepage-card-copy">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell homepage-final-cta-shell resource-final-shell">
            <div>
              <span className="homepage-label-chip">Ready to apply it?</span>
              <h2 className="homepage-section-title resource-section-title">See how Speak Your Menu turns allergen knowledge into safer daily service habits.</h2>
              <p className="homepage-section-body resource-section-body">
                The platform helps restaurants reinforce ingredient knowledge, tighten modification rules, and coach staff toward more consistent allergy answers across shifts.
              </p>
            </div>
            <div className="resource-final-actions">
              <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
              <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link">Open Q&amp;A guide</Link>
              <a href={qaGuideUrl} className="resource-tertiary-link">Share the Q&amp;A guide URL</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}